import {
  OTP_EXPIRATION_MS,
  createOtpRequestId,
  generateOtpCode,
} from "./otpService";
import { doc, updateDoc, query, collection, where, getDocs, Timestamp, runTransaction, limit } from "firebase/firestore";

const LOGIN_ATTEMPTS_KEY = "loginAttemptsByEmail";
const LOGIN_OTP_KEY = "loginOtpChallenge";
const PASSWORD_RESET_OTP_KEY = "passwordResetOtpChallenge";
const STAFF_2FA_VERIFIED_KEY = "staff2FAVerified";
const PENDING_REACTIVATION_KEY = "pendingAccountReactivation";

export const MAX_LOGIN_ATTEMPTS = 3;
export const LOGIN_LOCKOUT_MS = 5 * 60 * 1000;

const normalizeEmail = (email = "") => email.trim().toLowerCase();

const readJson = (storage, key) => {
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeJson = (storage, key, value) => {
  storage.setItem(key, JSON.stringify(value));
};

const getOtpStorage = (keepSignedIn) =>
  keepSignedIn ? localStorage : sessionStorage;

export const formatDuration = (ms) => {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

export const getLoginAttemptState = async (email, db, querySnapshot = null) => {
  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail || !db) {
    return { failedAttempts: 0, lockedUntil: null, isLocked: false, remainingMs: 0 };
  }

  try {
    // Use provided querySnapshot if available, otherwise perform query
    let snapshot = querySnapshot;
    if (!snapshot) {
      const usersQuery = query(
        collection(db, "users"),
        where("email", "==", normalizedEmail),
        limit(1) // Add limit - only need one document
      );
      snapshot = await getDocs(usersQuery);
    }
    
    if (snapshot.empty) {
      return { failedAttempts: 0, lockedUntil: null, isLocked: false, remainingMs: 0 };
    }

    const userDoc = snapshot.docs[0];
    const userData = userDoc.data();
    const locked = userData.locked || false;
    const lockedUntilRaw = userData.lockedUntil || null;
    const failedAttempts = userData.failedLoginAttempts || 0;

    // Convert Firestore Timestamp to milliseconds if needed
    const lockedUntil = lockedUntilRaw ? 
      (typeof lockedUntilRaw === 'number' ? lockedUntilRaw : lockedUntilRaw.toMillis()) : null;

    const remainingMs = lockedUntil ? lockedUntil - Date.now() : 0;

    // Return state without performing any writes (read-only)
    return {
      failedAttempts,
      lockedUntil,
      isLocked: locked && remainingMs > 0,
      remainingMs: Math.max(0, remainingMs),
    };
  } catch (error) {
    return { failedAttempts: 0, lockedUntil: null, isLocked: false, remainingMs: 0 };
  }
};

/**
 * Explicitly auto-unlock a user if their lock has expired.
 * Uses a transaction to prevent race conditions.
 * Should be called explicitly when a lock is detected as expired.
 * 
 * @param {string} email - User email
 * @param {object} db - Firestore database instance
 * @param {object} querySnapshot - Optional querySnapshot to reuse
 * @returns {Promise<boolean>} - True if unlock was performed, false otherwise
 */
export const maybeAutoUnlockUser = async (email, db, querySnapshot = null) => {
  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail || !db) {
    return false;
  }

  try {
    // Use provided querySnapshot if available, otherwise perform query
    let snapshot = querySnapshot;
    if (!snapshot) {
      const usersQuery = query(
        collection(db, "users"),
        where("email", "==", normalizedEmail),
        limit(1) // Add limit - only need one document
      );
      snapshot = await getDocs(usersQuery);
    }
    
    if (snapshot.empty) {
      return false;
    }

    const userDoc = snapshot.docs[0];
    const userId = userDoc.id;

    // Use transaction to prevent race conditions
    await runTransaction(db, async (transaction) => {
      const userRef = doc(db, "users", userId);
      const userSnap = await transaction.get(userRef);
      
      if (!userSnap.exists()) {
        return;
      }

      const userData = userSnap.data();
      const locked = userData.locked || false;
      const lockedUntilRaw = userData.lockedUntil || null;

      // Convert Firestore Timestamp to milliseconds if needed
      const lockedUntil = lockedUntilRaw ? 
        (typeof lockedUntilRaw === 'number' ? lockedUntilRaw : lockedUntilRaw.toMillis()) : null;

      const remainingMs = lockedUntil ? lockedUntil - Date.now() : 0;

      // Only unlock if currently locked and lock has expired
      if (locked && lockedUntil && remainingMs <= 0) {
        transaction.update(userRef, {
          locked: false,
          lockedUntil: null,
          failedLoginAttempts: 0,
        });
      }
    });

    return true;
  } catch (error) {
    return false;
  }
};

export const recordFailedLoginAttempt = async (email, db, querySnapshot = null) => {
  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail || !db) {
    return {
      failedAttempts: 0,
      lockedUntil: null,
      isLocked: false,
      remainingMs: 0,
    };
  }

  try {
    // Use provided querySnapshot if available, otherwise perform query
    let snapshot = querySnapshot;
    if (!snapshot) {
      const usersQuery = query(
        collection(db, "users"),
        where("email", "==", normalizedEmail),
        limit(1) // Add limit - only need one document
      );
      snapshot = await getDocs(usersQuery);
    }
    
    if (snapshot.empty) {
      return {
        failedAttempts: 0,
        lockedUntil: null,
        isLocked: false,
        remainingMs: 0,
      };
    }

    const userDoc = snapshot.docs[0];
    const userData = userDoc.data();
    const currentFailedAttempts = userData.failedLoginAttempts || 0;
    const failedAttempts = currentFailedAttempts + 1;
    const lockedUntilMs =
      failedAttempts >= MAX_LOGIN_ATTEMPTS ? Date.now() + LOGIN_LOCKOUT_MS : null;
    const isLocked = Boolean(lockedUntilMs);

    const userRef = doc(db, "users", userDoc.id);
    
    const updateData = {
      failedLoginAttempts: failedAttempts,
      locked: isLocked,
      lockedUntil: lockedUntilMs ? Timestamp.fromMillis(lockedUntilMs) : null,
    };

    await updateDoc(userRef, updateData, { merge: true });

    return {
      failedAttempts,
      lockedUntil: lockedUntilMs,
      isLocked,
      remainingMs: lockedUntilMs ? LOGIN_LOCKOUT_MS : 0,
    };
  } catch (error) {
    return {
      failedAttempts: 0,
      lockedUntil: null,
      isLocked: false,
      remainingMs: 0,
    };
  }
};

export const clearFailedLoginAttempts = async (email, db, querySnapshot = null) => {
  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail || !db) return;

  try {
    // Use provided querySnapshot if available, otherwise perform query
    let snapshot = querySnapshot;
    if (!snapshot) {
      const usersQuery = query(
        collection(db, "users"),
        where("email", "==", normalizedEmail),
        limit(1) // Add limit - only need one document
      );
      snapshot = await getDocs(usersQuery);
    }
    
    if (!snapshot.empty) {
      const userDoc = snapshot.docs[0];
      await updateDoc(doc(db, "users", userDoc.id), {
        failedLoginAttempts: 0,
        locked: false,
        lockedUntil: null,
      });
    }
  } catch (error) {
    // Error clearing failed login attempts
  }
};

export const clearStoredLoginOtpChallenge = () => {
  localStorage.removeItem(LOGIN_OTP_KEY);
  sessionStorage.removeItem(LOGIN_OTP_KEY);
};

export const setPendingAccountReactivation = ({ uid, email, userName, accountType }) => {
  const payload = {
    uid: uid || "",
    email: normalizeEmail(email || ""),
    userName: String(userName || "User").trim() || "User",
    accountType: String(accountType || "").trim(),
  };

  if (!payload.uid && !payload.email) {
    return null;
  }

  const payloadJson = JSON.stringify(payload);
  localStorage.setItem(PENDING_REACTIVATION_KEY, payloadJson);
  sessionStorage.setItem(PENDING_REACTIVATION_KEY, payloadJson);
  return payload;
};

export const getPendingAccountReactivation = () =>
  readJson(sessionStorage, PENDING_REACTIVATION_KEY) || readJson(localStorage, PENDING_REACTIVATION_KEY);

export const clearPendingAccountReactivation = () => {
  localStorage.removeItem(PENDING_REACTIVATION_KEY);
  sessionStorage.removeItem(PENDING_REACTIVATION_KEY);
};

export const shouldReactivateAfterSuccessfulLogin = ({ status, hasVerifiedLoginOtp }) => {
  const normalizedStatus = String(status || "").trim().toLowerCase();
  return normalizedStatus === "deactivated" && Boolean(hasVerifiedLoginOtp);
};

export const finalizePendingAccountReactivation = async ({
  uid,
  email,
  userName,
  db: firestoreDb,
  apiBase = process.env.REACT_APP_FUNCTIONS_URL || "https://us-central1-auravit-bb843.cloudfunctions.net",
}) => {
  const pending = getPendingAccountReactivation();
  const targetUid = uid || pending?.uid || "";
  const targetEmail = normalizeEmail(email || pending?.email || "");
  const targetUserName = String(userName || pending?.userName || "User").trim() || "User";

  if (!targetUid && !targetEmail) {
    return { success: false, reason: "no-pending-reactivation" };
  }

  try {
    if (firestoreDb && targetUid) {
      await updateDoc(doc(firestoreDb, "users", targetUid), {
        status: "active",
        reactivatedAt: Timestamp.fromDate(new Date()),
        lastLogin: Timestamp.fromDate(new Date()),
      }, { merge: true });
    } else if (firestoreDb && targetEmail) {
      const usersQuery = query(
        collection(firestoreDb, "users"),
        where("email", "==", targetEmail),
        limit(1)
      );
      const snapshot = await getDocs(usersQuery);
      if (!snapshot.empty) {
        const userDoc = snapshot.docs[0];
        await updateDoc(doc(firestoreDb, "users", userDoc.id), {
          status: "active",
          reactivatedAt: Timestamp.fromDate(new Date()),
          lastLogin: Timestamp.fromDate(new Date()),
        }, { merge: true });
      }
    }

    if (targetEmail) {
      await fetch(`${apiBase}/sendUserReactivationEmail`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userName: targetUserName,
          reactivationDate: new Date().toLocaleDateString(),
          userEmail: targetEmail,
        }),
      });
    }

    clearPendingAccountReactivation();
    return { success: true };
  } catch (error) {
    clearPendingAccountReactivation();
    return { success: false, error: error.message || String(error) };
  }
};

export const getStoredLoginOtpChallenge = () =>
  readJson(sessionStorage, LOGIN_OTP_KEY) || readJson(localStorage, LOGIN_OTP_KEY);

export const createStoredLoginOtpChallenge = ({
  email,
  otpDeliveryEmail,
  keepSignedIn,
  route,
  role,
  roles,
}) => {
  const normalized = normalizeEmail(email);
  
  // Use provided otpDeliveryEmail if given, otherwise fall back to the account email
  let deliveryEmail = otpDeliveryEmail || email;
  
  const challenge = {
    email: normalized,
    /** Where Nodemailer sends the OTP. */
    otpDeliveryEmail: normalizeEmail(deliveryEmail),
    otp: generateOtpCode(),
    expiresAt: Date.now() + OTP_EXPIRATION_MS,
    requestId: createOtpRequestId("login"),
    route,
    role: role || "",
    roles: Array.isArray(roles) ? roles : [],
    keepSignedIn: Boolean(keepSignedIn),
  };

  clearStoredLoginOtpChallenge();
  writeJson(getOtpStorage(keepSignedIn), LOGIN_OTP_KEY, challenge);
  return challenge;
};

export const refreshStoredLoginOtpChallenge = () => {
  const currentChallenge = getStoredLoginOtpChallenge();
  if (!currentChallenge?.email) {
    throw new Error("Verification session expired. Please log in again.");
  }

  const refreshedChallenge = {
    ...currentChallenge,
    otp: generateOtpCode(),
    expiresAt: Date.now() + OTP_EXPIRATION_MS,
    requestId: createOtpRequestId("login"),
  };

  clearStoredLoginOtpChallenge();
  writeJson(
    getOtpStorage(refreshedChallenge.keepSignedIn),
    LOGIN_OTP_KEY,
    refreshedChallenge
  );

  return refreshedChallenge;
};

export const verifyStoredLoginOtp = (otpCode) => {
  const currentChallenge = getStoredLoginOtpChallenge();

  if (!currentChallenge?.email) {
    return {
      success: false,
      error: "Verification session expired. Please log in again.",
    };
  }

  if (Date.now() > Number(currentChallenge.expiresAt || 0)) {
    return {
      success: false,
      error: "OTP expired. Please request a new code.",
    };
  }

  if (String(otpCode) !== String(currentChallenge.otp)) {
    return {
      success: false,
      error: "Incorrect code, please try again",
    };
  }

  return {
    success: true,
    challenge: currentChallenge,
  };
};

export const createPasswordResetOtpChallenge = (email) => {
  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail) {
    throw new Error("Email is required for password reset.");
  }

  const challenge = {
    email: normalizedEmail,
    otp: generateOtpCode(),
    expiresAt: Date.now() + OTP_EXPIRATION_MS,
    requestId: createOtpRequestId("password-reset"),
  };

  localStorage.setItem(PASSWORD_RESET_OTP_KEY, JSON.stringify(challenge));
  return challenge;
};

export const getStoredPasswordResetOtpChallenge = () => {
  return readJson(localStorage, PASSWORD_RESET_OTP_KEY);
};

export const clearStoredPasswordResetOtpChallenge = () => {
  localStorage.removeItem(PASSWORD_RESET_OTP_KEY);
};

export const verifyStoredPasswordResetOtp = (otpCode) => {
  const currentChallenge = getStoredPasswordResetOtpChallenge();
  if (!currentChallenge?.email) {
    return {
      success: false,
      error: "Verification session expired. Please request a new code.",
    };
  }

  if (Date.now() > Number(currentChallenge.expiresAt || 0)) {
    return {
      success: false,
      error: "OTP expired. Please request a new code.",
    };
  }

  if (String(otpCode) !== String(currentChallenge.otp)) {
    return {
      success: false,
      error: "Incorrect code, please try again",
    };
  }

  return { success: true, challenge: currentChallenge };
};

export const refreshStoredPasswordResetOtpChallenge = () => {
  const current = getStoredPasswordResetOtpChallenge();
  if (!current?.email) {
    throw new Error("Password reset session expired. Please start again.");
  }

  const refreshed = {
    ...current,
    otp: generateOtpCode(),
    expiresAt: Date.now() + OTP_EXPIRATION_MS,
    requestId: createOtpRequestId("password-reset"),
  };
  localStorage.setItem(PASSWORD_RESET_OTP_KEY, JSON.stringify(refreshed));
  return refreshed;
};

export const getLoginVerificationRoute = (email, accountType = "") => {
  const normalizedEmail = normalizeEmail(email);
  const normalizedType = (accountType || "").toString().toLowerCase();

  // STRICT: Account type from Firestore profile is the single source of truth
  if (normalizedType === "admin") {
    return { route: "/admin-verification", accountType: "Admin" };
  }
  if (normalizedType === "staff") {
    return { route: "/staff-verification", accountType: "Staff" };
  }
  if (normalizedType === "user") {
    return { route: "/user-verification", accountType: "User" };
  }

  // Fallback domain-based checks only if accountType is missing
  if (normalizedEmail.endsWith("@admin.auravit.com")) {
    return { route: "/user-verification", accountType: "Admin" };
  }

  if (normalizedEmail.endsWith("@staff.auravit.com")) {
    return { route: "/staff-verification", accountType: "Staff" };
  }

  return { route: "/user-verification", accountType: "User" };
};

// Staff 2FA verification session management
export const setStaff2FAVerified = () => {
  sessionStorage.setItem(STAFF_2FA_VERIFIED_KEY, "true");
};

export const isStaff2FAVerified = () => {
  return sessionStorage.getItem(STAFF_2FA_VERIFIED_KEY) === "true";
};

export const clearStaff2FAVerified = () => {
  sessionStorage.removeItem(STAFF_2FA_VERIFIED_KEY);
};
